import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HiBookOpen, HiFilter } from 'react-icons/hi'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair, seoBreadcrumbTriple } from '../../utils/seoUtils'
import './CompetitionRules.css'

function CompetitionRules() {
  const { t, i18n } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const { disciplineId } = useParams()
  const navigate = useNavigate()
  const [selectedDiscipline, setSelectedDiscipline] = useState(disciplineId || 'all')

  // Синхронизируем состояние с URL параметром
  useEffect(() => {
    setSelectedDiscipline(disciplineId || 'all')
  }, [disciplineId])
  
  const isJapanese = i18n.language === 'ja'
  const isRussian = i18n.language === 'ru'

  const getDisciplineName = (id) => {
    const names = {
      kubo: { ru: 'КУБО', en: 'KUBO', ja: 'КУБО' },
      aikido: { ru: 'Айкидо', en: 'Aikido', ja: '合気道' },
      aikijutsu: { ru: 'Айки-дзюцу', en: 'Aiki-jutsu', ja: '合気術' },
      jujutsu: { ru: 'Дзю-дзюцу', en: 'Jujutsu', ja: '柔術' },
      aikibujutsu: { ru: 'Айки-бу-дзюцу', en: 'Aiki-bu-jutsu', ja: '合気武術' },
      karatejutsu: { ru: 'Карате-дзюцу', en: 'Karate-jutsu', ja: '空手術' },
      kenjutsu: { ru: 'Кэн-дзюцу', en: 'Kenjutsu', ja: '剣術' },
      kempo: { ru: 'Кемпо', en: 'Kempo', ja: '拳法' }
    }
    if (id === 'all') return t('competitionRules.all', 'All Disciplines')
    const lang = isJapanese ? 'ja' : (isRussian ? 'ru' : 'en')
    return names[id] ? names[id][lang] : id
  }

  const disciplines = [
    { id: 'all', name: t('competitionRules.all', 'All Disciplines') },
    { id: 'kubo', name: getDisciplineName('kubo') },
    { id: 'aikido', name: getDisciplineName('aikido') },
    { id: 'aikijutsu', name: getDisciplineName('aikijutsu') },
    { id: 'jujutsu', name: getDisciplineName('jujutsu') },
    { id: 'aikibujutsu', name: getDisciplineName('aikibujutsu') },
    { id: 'karatejutsu', name: getDisciplineName('karatejutsu') },
    { id: 'kenjutsu', name: getDisciplineName('kenjutsu') },
    { id: 'kempo', name: getDisciplineName('kempo') }
  ]

  const breadcrumbItems = useMemo(() => {
    const home = t('common.home')
    const page = t('common.competitionRules')
    if (!disciplineId || selectedDiscipline === 'all') {
      return seoBreadcrumbPair(home, page, 'competition-rules')
    }
    return seoBreadcrumbTriple(
      home,
      page,
      'competition-rules',
      getDisciplineName(disciplineId),
      `competition-rules/${disciplineId}`
    )
  }, [t, disciplineId, selectedDiscipline, isJapanese, isRussian])

  return (
    <div className="competition-rules-page">
      <SEO 
        title={t('common.competitionRules')}
        description={t('competitionRules.description', 'Competition rules and regulations for SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="competition-rules-hero">
          <h1>{t('common.competitionRules')}</h1>
          <p className="competition-rules-intro">
            {t('competitionRules.intro', 'Official competition rules and regulations for all martial arts disciplines within SUMA International.')}
          </p>
        </div>

        <div className="rules-filter">
          <HiFilter className="filter-icon" />
          <select
            value={selectedDiscipline}
            onChange={(e) => {
              const newDiscipline = e.target.value
              setSelectedDiscipline(newDiscipline)
              if (newDiscipline === 'all') {
                navigate(getLocalizedLink('/competition-rules'))
              } else {
                navigate(`${getLocalizedLink('/competition-rules')}/${newDiscipline}`)
              }
            }}
            className="discipline-select"
          >
            {disciplines.map(discipline => (
              <option key={discipline.id} value={discipline.id}>
                {discipline.name}
              </option>
            ))}
          </select>
        </div>

        <section className="rules-section">
          <h2>{t('competitionRules.general', 'General Rules')}</h2>
          <div className="rules-content">
            <p>{t('competitionRules.generalText', 'General competition rules apply to all disciplines and include safety requirements, participant eligibility, equipment standards, and judging criteria.')}</p>
            <div className="general-rules-list">
              <div className="rule-item">
                <h3>{t('competitionRules.safety', 'Safety Requirements')}</h3>
                <p>{t('competitionRules.safetyText', 'All participants must wear appropriate protective equipment. Medical clearance may be required for certain competitions.')}</p>
              </div>
              <div className="rule-item">
                <h3>{t('competitionRules.eligibility', 'Participant Eligibility')}</h3>
                <p>{t('competitionRules.eligibilityText', 'Participants must be members of SUMA International schools or federations and meet age and rank requirements for their category.')}</p>
              </div>
              <div className="rule-item">
                <h3>{t('competitionRules.equipment', 'Equipment Standards')}</h3>
                <p>{t('competitionRules.equipmentText', 'All equipment must meet SUMA International standards and be approved by competition officials before use.')}</p>
              </div>
              <div className="rule-item">
                <h3>{t('competitionRules.judging', 'Judging Criteria')}</h3>
                <p>{t('competitionRules.judgingText', 'Competitions are judged by certified officials according to discipline-specific criteria including technique, execution, and sportsmanship.')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="rules-section">
          <h2>{t('competitionRules.disciplineRules', 'Discipline-Specific Rules')}</h2>
          <div className="discipline-rules-grid">
            {disciplines.filter(d => d.id !== 'all').map(discipline => (
              <div key={discipline.id} className="discipline-rule-card">
                <HiBookOpen className="rule-icon" />
                <h3>{discipline.name}</h3>
                <p>{t('competitionRules.ruleCardText', 'Detailed competition rules for this discipline')}</p>
                <Link to={`${getLocalizedLink('/competition-rules')}/${discipline.id}`} className="rule-link">
                  {t('competitionRules.viewRules', 'View Rules')} →
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="rules-section">
          <h2>{t('competitionRules.categories', 'Age and Weight Categories')}</h2>
          <p>{t('competitionRules.categoriesText', 'Competitions are organized by age groups and weight categories to ensure fair competition.')}</p>
          <div className="categories-info">
            <div className="category-group">
              <h3>{t('competitionRules.ageGroups', 'Age Groups')}</h3>
              <ul>
                <li>{t('competitionRules.children', 'Children: 6-12 years')}</li>
                <li>{t('competitionRules.juniors', 'Juniors: 13-17 years')}</li>
                <li>{t('competitionRules.adults', 'Adults: 18-35 years')}</li>
                <li>{t('competitionRules.seniors', 'Seniors: 36+ years')}</li>
              </ul>
            </div>
            <div className="category-group">
              <h3>{t('competitionRules.weightCategories', 'Weight Categories')}</h3>
              <p>{t('competitionRules.weightCategoriesText', 'Weight categories vary by discipline and gender. Specific categories are defined in discipline-specific rules.')}</p>
            </div>
          </div>
        </section>

        <section className="rules-section">
          <h2>{t('competitionRules.documents', 'Official Documents')}</h2>
          <p>{t('competitionRules.documentsText', 'For complete and official competition rules, please refer to the Documents section.')}</p>
          <Link to={getLocalizedLink('/documents')} className="btn btn-primary">
            {t('common.documents')}
          </Link>
        </section>
      </div>
    </div>
  )
}

export default CompetitionRules
